import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PersonRounded from "@mui/icons-material/PersonRounded";
import SendRounded from "@mui/icons-material/SendRounded";
import SmartToyRounded from "@mui/icons-material/SmartToyRounded";
import { grey } from "@mui/material/colors";
import * as d3 from "d3";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { getLookerAgentId, LOOKER_AGENT_PAGE_KEYS } from "../lookerAgentConfig";

const defaultInitialMessage = "";
const MAX_VISIBLE_MESSAGES = 30;
const MAX_TABLE_ROWS = 50;
const MAX_CHART_ROWS = 40;
const getSuggestedQuestions = (data) => {
	const possibleValues = [
		data?.suggested_questions,
		data?.suggestedQuestions,
		data?.questions,
		data?.sample_questions,
		data?.sampleQuestions,
		data?.follow_up_questions,
		data?.followUpQuestions,
		data?.prompts,
	];
	const list = possibleValues.find((value) => Array.isArray(value) && value.length);
	if (!list) return [];

	return list
		.map((item) => {
			if (typeof item === "string") return item;
			return item?.question || item?.text || item?.label || item?.prompt || "";
		})
		.map((item) => item.trim())
		.filter(Boolean)
		.slice(0, 4);
};

const formatLabel = (value) => {
	return String(value || "")
		.split(".")
		.pop()
		.split("_")
		.join(" ");
};

const formatValue = (value) => {
	if (value === null || value === undefined || value === "") return "--";
	if (typeof value === "number") {
		return new Intl.NumberFormat("en-IN", {
			maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
		}).format(value);
	}
	if (typeof value === "object") return JSON.stringify(value);
	return value;
};

const getNumber = (value) => {
	const numberValue = Number(value);
	return Number.isFinite(numberValue) ? numberValue : 0;
};

const getChartYKeys = (chart) => {
	if (Array.isArray(chart?.yKeys) && chart.yKeys.length) return chart.yKeys;
	if (chart?.yKey) return [chart.yKey];
	if (chart?.valueKey) return [chart.valueKey];
	return [];
};

const chartPalette = ["#2563eb", "#16a34a", "#f59e0b", "#dc2626", "#7c3aed", "#0891b2"];

const truncateLabel = (label, length = 18) => {
	const text = String(label || "");
	return text.length > length ? `${text.slice(0, length - 1)}...` : text;
};

const ResultChart = ({ chart, rows }) => {
	if (!chart || !Array.isArray(rows) || rows.length === 0) return null;

	const rawType = String(chart.type || "bar").trim().toLowerCase();
	const type = rawType === "column" ? "bar" : rawType;
	const xKey = chart.xKey || chart.labelKey;
	const yKeys = getChartYKeys(chart);
	if (!xKey || !yKeys.length) return null;

	const chartRows = rows.slice(0, MAX_CHART_ROWS);
	const width = 920;
	const height = Math.max(Number(chart.height) || 320, 280);
	const margin = { top: 20, right: 28, bottom: 72, left: 84 };
	const innerWidth = width - margin.left - margin.right;
	const innerHeight = height - margin.top - margin.bottom;
	const labels = chartRows.map((row) => String(formatValue(row[xKey])));
	const chartTitle = chart.title || `${formatLabel(yKeys[0])} by ${formatLabel(xKey)}`;

	if (type === "pie") {
		const valueKey = yKeys[0];
		const pieRows = chartRows
			.map((row) => ({ label: String(formatValue(row[xKey])), value: getNumber(row[valueKey]) }))
			.filter((row) => row.value > 0);
		if (!pieRows.length) return null;

		const radius = Math.min(innerWidth, innerHeight) / 2;
		const arc = d3.arc().innerRadius(radius * 0.52).outerRadius(radius);
		const labelArc = d3.arc().innerRadius(radius * 0.74).outerRadius(radius * 0.74);
		const pie = d3.pie().value((item) => item.value).sort(null)(pieRows);
		const total = d3.sum(pieRows, (item) => item.value);

		return (
			<Box sx={{ mt: 1, p: 1.5, border: `1px solid ${grey[300]}`, borderRadius: 1, backgroundColor: "common.white" }}>
				<Typography fontSize={13} fontWeight={700} sx={{ mb: 1 }}>
					{chartTitle}
				</Typography>
				<svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img" aria-label={chartTitle}>
					<rect width={width} height={height} rx="8" fill="#ffffff" />
					<g transform={`translate(${width / 2 - 120}, ${margin.top + innerHeight / 2})`}>
						{pie.map((slice, index) => {
							const [labelX, labelY] = labelArc.centroid(slice);
							const percent = total ? Math.round((slice.data.value / total) * 100) : 0;
							return (
								<g key={slice.data.label}>
									<path d={arc(slice)} fill={chartPalette[index % chartPalette.length]} stroke="#fff" strokeWidth="2" style={{ cursor: "pointer" }}>
										<title>{[slice.data.label, `Value: ${formatValue(slice.data.value)}`, `Share: ${percent}%`].join("\n")}</title>
									</path>
									{percent >= 8 && (
										<text x={labelX} y={labelY} textAnchor="middle" dominantBaseline="middle" fontSize="12" fontWeight="700" fill="#fff" pointerEvents="none">
											{percent}%
										</text>
									)}
								</g>
							);
						})}
					</g>
					<g transform={`translate(${width - 270}, ${margin.top + 12})`}>
						{pieRows.slice(0, 8).map((row, index) => (
							<g key={row.label} transform={`translate(0, ${index * 26})`}>
								<rect width="12" height="12" rx="3" fill={chartPalette[index % chartPalette.length]} />
								<text x="20" y="10" fontSize="12" fill="#374151">
									{truncateLabel(row.label, 24)} - {formatValue(row.value)}
								</text>
							</g>
						))}
					</g>
				</svg>
			</Box>
		);
	}

	const values = chartRows.flatMap((row) => yKeys.map((key) => getNumber(row[key])));
	const maxValue = Math.max(d3.max(values) || 0, 1);
	const xScale = d3.scaleBand().domain(labels).range([0, innerWidth]).padding(0.22);
	const yScale = d3.scaleLinear().domain([0, maxValue]).nice().range([innerHeight, 0]);
	const yTicks = yScale.ticks(5);
	const xTickStep = Math.max(1, Math.ceil(labels.length / 10));

	return (
		<Box sx={{ mt: 1, p: 1.5, border: `1px solid ${grey[300]}`, borderRadius: 1, backgroundColor: "common.white" }}>
			<Typography fontSize={13} fontWeight={700} sx={{ mb: 1 }}>
				{chartTitle}
			</Typography>
			<svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img" aria-label={chartTitle}>
				<rect width={width} height={height} rx="8" fill="#ffffff" />
				<g transform={`translate(${margin.left}, ${margin.top})`}>
					{yTicks.map((tick) => (
						<g key={tick} transform={`translate(0, ${yScale(tick)})`}>
							<line x1="0" x2={innerWidth} stroke="#e5e7eb" strokeDasharray="4 6" />
							<text x="-12" y="4" textAnchor="end" fontSize="11" fill="#6b7280">
								{formatValue(tick)}
							</text>
						</g>
					))}
					<line y1={innerHeight} y2={innerHeight} x1="0" x2={innerWidth} stroke="#d1d5db" />
					<line x1="0" x2="0" y1="0" y2={innerHeight} stroke="#d1d5db" />

					{type === "line" ? (
						yKeys.map((key, keyIndex) => {
							const line = d3
								.line()
								.x((row) => (xScale(String(formatValue(row[xKey]))) || 0) + xScale.bandwidth() / 2)
								.y((row) => yScale(getNumber(row[key])))
								.curve(d3.curveMonotoneX);
							return (
								<g key={key}>
									<path d={line(chartRows)} fill="none" stroke={chartPalette[keyIndex % chartPalette.length]} strokeWidth="3" />
									{chartRows.map((row) => {
										const label = String(formatValue(row[xKey]));
										const value = getNumber(row[key]);
										return (
											<circle key={`${key}-${label}`} cx={(xScale(label) || 0) + xScale.bandwidth() / 2} cy={yScale(value)} r="4.5" fill="#fff" stroke={chartPalette[keyIndex % chartPalette.length]} strokeWidth="2" style={{ cursor: "pointer" }}>
												<title>{[label, `${formatLabel(key)}: ${formatValue(value)}`].join("\n")}</title>
											</circle>
										);
									})}
								</g>
							);
						})
					) : (
						chartRows.map((row) => {
							const label = String(formatValue(row[xKey]));
							const groupX = xScale(label) || 0;
							const barWidth = xScale.bandwidth() / Math.max(yKeys.length, 1);
							return yKeys.map((key, keyIndex) => {
								const value = getNumber(row[key]);
								return (
									<rect
										key={`${label}-${key}`}
										x={groupX + keyIndex * barWidth + 2}
										y={yScale(value)}
										width={Math.max(barWidth - 4, 2)}
										height={innerHeight - yScale(value)}
										rx="4"
										fill={chartPalette[keyIndex % chartPalette.length]}
										style={{ cursor: "pointer" }}>
										<title>{[label, `${formatLabel(key)}: ${formatValue(value)}`].join("\n")}</title>
									</rect>
								);
							});
						})
					)}

					{labels.map((label, index) => {
						if (index % xTickStep !== 0) return null;
						return (
							<text key={label} x={(xScale(label) || 0) + xScale.bandwidth() / 2} y={innerHeight + 22} textAnchor="end" fontSize="11" fill="#6b7280" transform={`rotate(-35, ${(xScale(label) || 0) + xScale.bandwidth() / 2}, ${innerHeight + 22})`}>
								{truncateLabel(label, 16)}
							</text>
						);
					})}
				</g>
				<g transform={`translate(${margin.left}, ${height - 24})`}>
					{yKeys.map((key, index) => (
						<g key={key} transform={`translate(${index * 170}, 0)`}>
							<rect width="12" height="12" rx="3" fill={chartPalette[index % chartPalette.length]} />
							<text x="18" y="10" fontSize="12" fill="#374151">
								{truncateLabel(chart.yLabel || formatLabel(key), 20)}
							</text>
						</g>
					))}
				</g>
			</svg>
		</Box>
	);
};

const ResultTable = ({ rows }) => {
	if (!Array.isArray(rows) || rows.length === 0) return null;

	const columns = Object.keys(rows[0] || {});
	const tableRows = rows.slice(0, MAX_TABLE_ROWS);
	if (!columns.length) return null;

	return (
		<TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 280 }}>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow>
						{columns.map((column) => (
							<TableCell key={column} sx={{ fontWeight: 700, whiteSpace: "nowrap", textTransform: "capitalize" }}>
								{formatLabel(column)}
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{tableRows.map((row, rowIndex) => (
						<TableRow key={rowIndex} hover>
							{columns.map((column) => (
								<TableCell key={column} sx={{ whiteSpace: "nowrap" }}>
									{formatValue(row[column])}
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
const InlineText = ({ text }) => {
	const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);

	return parts.map((part, index) => {
		if (part.startsWith("**") && part.endsWith("**")) {
			return (
				<Box key={index} component="span" sx={{ fontWeight: 700 }}>
					{part.slice(2, -2)}
				</Box>
			);
		}
		if (part.startsWith("*") && part.endsWith("*")) {
			return (
				<Box key={index} component="span" sx={{ fontStyle: "italic" }}>
					{part.slice(1, -1)}
				</Box>
			);
		}
		return part;
	});
};

const MarkdownText = ({ text, color = "inherit" }) => {
	const lines = String(text || "").split("\n");

	return (
		<Stack spacing={0.5}>
			{lines.map((line, index) => {
				const trimmed = line.trim();
				if (!trimmed) return <Box key={index} sx={{ height: 4 }} />;

				const heading = trimmed.match(/^(#{1,6})\s+(.+)$/);
				if (heading) {
					return (
						<Typography key={index} fontSize={15} fontWeight={700} color={color} sx={{ mt: index ? 0.5 : 0 }}>
							<InlineText text={heading[2]} />
						</Typography>
					);
				}

				const bullet = trimmed.match(/^[-*]\s+(.+)$/);
				if (bullet) {
					return (
						<Box key={index} display="flex" gap={1} alignItems="flex-start">
							<Typography fontSize={14} color={color} lineHeight={1.5}>
								-
							</Typography>
							<Typography fontSize={14} color={color} lineHeight={1.5} sx={{ overflowWrap: "anywhere" }}>
								<InlineText text={bullet[1]} />
							</Typography>
						</Box>
					);
				}

				return (
					<Typography key={index} fontSize={14} color={color} lineHeight={1.5} sx={{ overflowWrap: "anywhere" }}>
						<InlineText text={trimmed} />
					</Typography>
				);
			})}
		</Stack>
	);
};
const EmptyChatState = ({ title }) => (
	<Stack alignItems="center" justifyContent="center" spacing={2} sx={{ flex: 1, minHeight: 360, px: 2, textAlign: "center" }}>
		<Box
			sx={{
				width: 82,
				height: 82,
				borderRadius: "50%",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				backgroundColor: grey[900],
				color: "common.white",
				boxShadow: "0 14px 34px rgba(17, 24, 39, 0.18)",
			}}>
			<SmartToyRounded sx={{ fontSize: 42 }} />
		</Box>
		<Stack spacing={0.75} sx={{ maxWidth: 560 }}>
			<Typography fontSize={24} fontWeight={800} color="text.primary">
				{title}
			</Typography>
			<Typography fontSize={14} color="text.secondary">
				Connecting to the assistant to load available questions.
			</Typography>
		</Stack>
	</Stack>
);
const SuggestedQuestions = ({ questions, onPickQuestion, disabled }) => {
	if (!questions.length) return null;

	return (
		<Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ pl: { xs: 0, sm: 5 }, pr: 1, flexWrap: "wrap" }}>
			{questions.map((item) => (
				<Button
					key={item}
					variant="outlined"
					disabled={disabled}
					onClick={() => onPickQuestion(item)}
					sx={{
						borderColor: grey[300],
						color: grey[900],
						backgroundColor: "common.white",
						justifyContent: "flex-start",
						textAlign: "left",
						textTransform: "none",
						minHeight: 38,
						whiteSpace: "normal",
						"&:hover": { borderColor: grey[500], backgroundColor: grey[100] },
					}}>
					{item}
				</Button>
			))}
		</Stack>
	);
};

const MessageBubble = memo(({ message }) => {
	const isUser = message.role === "user";
	const hasResult = !isUser && (message.chart || (Array.isArray(message.rows) && message.rows.length > 0));

	return (
		<Box display="flex" justifyContent={isUser ? "flex-end" : "flex-start"}>
			<Stack direction={isUser ? "row-reverse" : "row"} spacing={1} maxWidth={hasResult ? "min(1120px, 96%)" : "82%"} width={hasResult ? "100%" : "auto"} alignItems="flex-start">
				<Box
					sx={{
						width: 32,
						height: 32,
						borderRadius: "50%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						backgroundColor: isUser ? grey[900] : grey[300],
						color: isUser ? "common.white" : grey[800],
						flex: "0 0 auto",
					}}>
					{isUser ? <PersonRounded fontSize="small" /> : <SmartToyRounded fontSize="small" />}
				</Box>
				<Box
					sx={{
						backgroundColor: isUser ? grey[900] : "common.white",
						color: isUser ? "common.white" : "text.primary",
						border: isUser ? "none" : `1px solid ${grey[300]}`,
						borderRadius: 1,
						px: 1.5,
						py: 1,
						boxShadow: isUser ? 0 : 1,
						minWidth: 0,
						width: hasResult ? "100%" : "auto",
					}}>
					<MarkdownText text={message.content} color={isUser ? "common.white" : "text.primary"} />
					<ResultChart chart={message.chart} rows={message.rows} />
					<ResultTable rows={message.rows} />
				</Box>
			</Stack>
		</Box>
	);
});

const LookerChatBot = ({
	title = "Rare Chat Bot",
	initialMessage = defaultInitialMessage,
	placeholder = "Ask your query...",
	apiPath = "/api/reports/looker-chat-bot/query",
	agentId = getLookerAgentId(LOOKER_AGENT_PAGE_KEYS.RARE_CHAT_BOT),
}) => {
	const [messages, setMessages] = useState(() => (initialMessage ? [{ role: "assistant", content: initialMessage }] : []));
	const [question, setQuestion] = useState("");
	const [loading, setLoading] = useState(false);
	const [suggestedQuestions, setSuggestedQuestions] = useState([]);
	const inputRef = useRef(null);
	const messagesContainerRef = useRef(null);
	const latestAssistantMessageRef = useRef(null);
	const previousMessageCountRef = useRef(messages.length);
	const pendingAnswerScrollRef = useRef(false);
	const greetingLoadedRef = useRef(false);

	useEffect(() => {
		const messageCountChanged = previousMessageCountRef.current !== messages.length;
		previousMessageCountRef.current = messages.length;
		if (!messageCountChanged) return undefined;

		const latestMessage = messages[messages.length - 1];
		const scrollToLatestMessage = () => {
			const messagesContainer = messagesContainerRef.current;
			if (!messagesContainer) return;

			if (latestMessage?.role === "assistant" && pendingAnswerScrollRef.current && latestAssistantMessageRef.current) {
				const nextTop = latestAssistantMessageRef.current.offsetTop - messagesContainer.offsetTop - 8;
				messagesContainer.scrollTo({ top: Math.max(nextTop, 0), behavior: "auto" });
				return;
			}

			if (latestMessage?.role === "user") {
				messagesContainer.scrollTo({ top: messagesContainer.scrollHeight, behavior: "auto" });
			}
		};

		const frame = requestAnimationFrame(scrollToLatestMessage);
		const timeout = setTimeout(() => {
			scrollToLatestMessage();
			if (latestMessage?.role === "assistant") pendingAnswerScrollRef.current = false;
		}, 120);

		return () => {
			cancelAnimationFrame(frame);
			clearTimeout(timeout);
		};
	}, [messages]);

	const visibleMessages = useMemo(() => messages.slice(-MAX_VISIBLE_MESSAGES), [messages]);
	const hiddenMessagesCount = Math.max(messages.length - visibleMessages.length, 0);

	useEffect(() => {
		if (initialMessage || greetingLoadedRef.current) return undefined;

		let isActive = true;
		greetingLoadedRef.current = true;
		setLoading(true);

		const loadGreeting = async () => {
			try {
				const requestPayload = {
					question: "hi",
					history: [{ role: "user", content: "hi" }],
				};
				if (agentId) requestPayload.agent_id = agentId;

				const res = await pythonVmInstance.post(apiPath, requestPayload);
				if (!isActive) return;

				setSuggestedQuestions(getSuggestedQuestions(res?.data));
				setMessages([
					{
						role: "assistant",
						content: res?.data?.answer || "Hi, how can I help you?",
						rows: res?.data?.rows || [],
						sql: res?.data?.sql || "",
						chart: res?.data?.chart || null,
					},
				]);
			} catch (err) {
				if (!isActive) return;
				setMessages([
					{
						role: "assistant",
						content: err?.response?.data?.detail || "Unable to load Rare Chat Bot right now.",
					},
				]);
			} finally {
				if (isActive) setLoading(false);
			}
		};

		loadGreeting();

		return () => {
			isActive = false;
		};
	}, [agentId, apiPath, initialMessage]);

	const sendQuestion = async (questionOverride) => {
		const trimmedQuestion = String(questionOverride || question).trim();
		if (!trimmedQuestion || loading) return;

		pendingAnswerScrollRef.current = true;
		setSuggestedQuestions([]);
		const userMessage = { role: "user", content: trimmedQuestion };
		const nextMessages = [...messages, userMessage];
		setMessages(nextMessages);
		setQuestion("");
		setLoading(true);

		try {
			const history = nextMessages
				.filter((message) => message.role === "user" || message.role === "assistant")
				.slice(-8)
				.map(({ role, content }) => ({ role, content }));

			const requestPayload = {
				question: trimmedQuestion,
				history,
			};
			if (agentId) requestPayload.agent_id = agentId;

			const res = await pythonVmInstance.post(apiPath, requestPayload);
			setSuggestedQuestions(getSuggestedQuestions(res?.data));

			setMessages((prev) => [
				...prev,
				{
					role: "assistant",
					content: res?.data?.answer || "No answer returned.",
					rows: res?.data?.rows || [],
					sql: res?.data?.sql || "",
					chart: res?.data?.chart || null,
				},
			]);
		} catch (err) {
			setMessages((prev) => [
				...prev,
				{
					role: "assistant",
					content: err?.response?.data?.detail || "Unable to answer this question right now.",
				},
			]);
		} finally {
			setLoading(false);
			setTimeout(() => inputRef.current?.focus(), 0);
		}
	};

	const handleStarterQuestion = (starterQuestion) => {
		sendQuestion(starterQuestion);
	};
	const handleKeyDown = (event) => {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			sendQuestion();
		}
	};

	return (
		<Stack
			width="100%"
			height="calc(100% - 12px)"
			sx={{
				backgroundColor: grey[200],
				p: 1.25,
				boxSizing: "border-box",
				overflow: "hidden",
			}}>
			<Stack
				sx={{
					flex: 1,
					minHeight: 0,
					width: "100%",
					backgroundColor: grey[50],
					border: `1px solid ${grey[300]}`,
					borderRadius: 1,
					overflow: "hidden",
				}}>
				{/* <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${grey[300]}`, backgroundColor: "common.white" }}>
					<Typography variant="h6" fontSize={18}>
						{title}
					</Typography>
				</Box> */}

				<Stack ref={messagesContainerRef} spacing={1.5} sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 1.5, overscrollBehavior: "contain" }}>
					{hiddenMessagesCount > 0 && (
						<Box sx={{ alignSelf: "center", px: 1.5, py: 0.75, borderRadius: 1, backgroundColor: grey[200], color: "text.secondary" }}>
							<Typography fontSize={12}>{hiddenMessagesCount} older messages hidden for performance</Typography>
						</Box>
					)}
					{visibleMessages.length === 0 && !loading && <EmptyChatState title={title} />}
					{visibleMessages.map((message, index) => {
						const isLatestAnswer = index === visibleMessages.length - 1 && message.role === "assistant";

						return (
							<Box key={`${message.role}-${hiddenMessagesCount + index}`} ref={isLatestAnswer ? latestAssistantMessageRef : null}>
								<MessageBubble message={message} />
							</Box>
						);
					})}
					<SuggestedQuestions questions={suggestedQuestions} onPickQuestion={handleStarterQuestion} disabled={loading} />
					{loading && (
						<Box display="flex" alignItems="center" gap={1} color="text.secondary" pl={5}>
							<CircularProgress size={18} />
							<Typography fontSize={13}>Thinking</Typography>
						</Box>
					)}
				</Stack>

				<Box sx={{ p: 1.5, borderTop: `1px solid ${grey[300]}`, backgroundColor: "common.white" }}>
					<Stack direction="row" spacing={1} alignItems="flex-end">
						<TextField
							inputRef={inputRef}
							fullWidth
							multiline
							maxRows={4}
							size="small"
							placeholder={placeholder}
							value={question}
							onChange={(event) => setQuestion(event.target.value)}
							onKeyDown={handleKeyDown}
							disabled={loading}
						/>
						<Button
							variant="contained"
							onClick={() => sendQuestion()}
							disabled={loading || !question.trim()}
							sx={{ minWidth: 42, height: 40 }}>
							<SendRounded fontSize="small" />
						</Button>
					</Stack>
				</Box>
			</Stack>
		</Stack>
	);
};

export default LookerChatBot;


















